import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { GraduationCap, Heart, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { Reveal, Eyebrow, MaskLine, MarqueeBand } from "@/components/editorial";
import { MagneticButton } from "@/components/MagneticButton";

const values = [
  {
    n: "01",
    icon: Heart,
    title: "Non-judgmental",
    body: "You aren't behind. Real life is messy. These tools meet you where you actually are.",
  },
  {
    n: "02",
    icon: GraduationCap,
    title: "Rooted in psychology",
    body: "Built on executive function research, externalize what your brain shouldn't have to carry.",
  },
  {
    n: "03",
    icon: Sparkles,
    title: "Practical, not perfect",
    body: "Simple checklists you'll actually use beat elaborate systems you won't.",
  },
];

export default function AboutPage() {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const heroRef = useRef(null);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.06]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Please add your first name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/subscribers", { ...form, source: "about-page" });
      setDone(true);
      toast.success("You're in! Check your inbox for the Weekly Reset.");
      setForm({ first_name: "", email: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="about-page" className="relative">
      <Seo
        title="About Lisa"
        description="Hi, I'm Lisa, psychology graduate, elementary school parent, and creator of One Step Ahead Parents. Helping parents create systems that reduce overwhelm and make school life easier."
        path="/about"
      />

      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="about-hero"
        className="relative overflow-hidden pt-14 md:pt-20 pb-24 md:pb-32"
      >
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute inset-0 bg-brand-bg" />
          <div className="absolute -top-32 -right-40 w-[520px] h-[520px] rounded-full bg-brand-sage/12 blur-[110px]" />
          <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] rounded-full bg-brand-accent/10 blur-[110px]" />
          <div className="absolute inset-0 grain opacity-40" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="flex items-center gap-2"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" />
              Vol. 03, the founder
            </motion.span>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden md:inline"
            >
              A note from Lisa
            </motion.span>
          </div>

          <div className="mt-10 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-end">
            {/* Portrait */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-8 -left-3 z-20 select-none pointer-events-none">
                  <span className="font-editorial italic text-brand-accent text-6xl md:text-8xl leading-none">
                    03
                  </span>
                </div>
                <motion.div
                  style={{ y: imgY, scale: imgScale }}
                  className="spotlight-frame relative rounded-[28px] overflow-hidden aspect-[4/5] w-full bg-brand-sage/10"
                >
                  <img
                    src="/images/lisa-couch.png"
                    alt="Lisa, founder of One Step Ahead Parents, sitting on a couch at home"
                    data-testid="about-profile-photo"
                    loading="eager"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-brand-ink/10 via-transparent to-transparent" />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  className="absolute -right-3 top-6 rotate-90 origin-top-right hidden lg:block"
                >
                  <span className="text-[10px] uppercase tracking-[0.3em] text-brand-inkMuted">
                    Fig. 03, the founder
                  </span>
                </motion.div>
              </div>
            </div>

            {/* Copy */}
            <div className="lg:col-span-7">
              <h1 className="font-display font-medium tracking-[-0.03em] text-brand-ink leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[96px]">
                <span className="block"><MaskLine delay={0.05}>Hi,</MaskLine></span>
                <span className="block"><MaskLine delay={0.16}>I&apos;m <span className="font-editorial italic font-normal text-brand-sage">Lisa.</span></MaskLine></span>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 text-brand-inkSoft text-base sm:text-lg leading-relaxed max-w-xl"
              >
                I&apos;m a psychology graduate, a mom of boys, and the creator of One Step Ahead Parents. I started this because I kept missing stuff; picture day, spirit week, the form due Tuesday.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8"
              >
                <MagneticButton>
                  <Link
                    to="/products"
                    data-testid="about-cta"
                    className="group inline-flex items-center gap-3 bg-brand-ink text-brand-bg hover:bg-brand-sageDark transition-colors px-7 py-4 rounded-full font-medium"
                  >
                    Browse the systems
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </MagneticButton>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <MarqueeBand items={["Psychology, not planners", "Systems, not memory", "Real life, not perfect", "Built for parents"]} />

      {/* STORY IN CHAPTERS */}
      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>My story, in three parts</Eyebrow>
          </Reveal>

          <div className="mt-14 md:mt-20 space-y-24 md:space-y-32">
            {[
              {
                n: "01",
                kicker: "The unraveling",
                body: (
                  <>
                    Somewhere between the parent portal, three classroom apps, PTO emails (I sit on the board, so I see them from both sides), the lunch menu PDF, and our family group text, the wheels would come off. And I noticed something:{" "}
                    <span className="text-brand-ink font-medium">no one had taught me a system for this.</span>
                  </>
                ),
              },
              {
                n: "02",
                kicker: "The prototype",
                body: (
                  <>
                    So I built one. Then I tested it on my own family. Then I refined it using everything I knew about executive function and behavior change. The tools you find here are the result; practical, repeatable, and designed for real parents with real schedules.
                  </>
                ),
              },
              {
                n: "03",
                kicker: "The mission",
                body: (
                  <>
                    My psychology background, and everything I&apos;ve learned about executive function, cognitive load, and behavior change, is baked into every tool. These aren&apos;t generic planners; they&apos;re built around how the parent brain actually works when it&apos;s managing too many inputs.
                  </>
                ),
              },
            ].map((c) => (
              <div key={c.n} className="grid grid-cols-12 gap-6 md:gap-10 items-start">
                <Reveal delay={0.05} className="col-span-12 md:col-span-3">
                  <div className="flex md:block items-baseline gap-4">
                    <span className="font-editorial italic text-brand-accent text-[96px] md:text-[140px] leading-[0.85]">
                      {c.n}
                    </span>
                    <span className="md:mt-4 block text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-sage">
                      {c.kicker}
                    </span>
                  </div>
                </Reveal>
                <Reveal delay={0.15} className="col-span-12 md:col-span-9 md:pl-8 md:border-l md:border-brand-border">
                  <p className="font-display font-normal tracking-[-0.01em] text-brand-ink text-2xl sm:text-3xl md:text-4xl leading-[1.2] max-w-3xl">
                    {c.body}
                  </p>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES on dark band */}
      <section className="relative bg-brand-ink text-brand-bg overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 grain opacity-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-sage">
              04, what you&apos;ll find here
            </p>
            <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-4xl md:text-5xl leading-[1.02] max-w-2xl">
              Every tool follows <span className="font-editorial italic text-brand-accent">three</span> quiet rules.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {values.map((v, i) => (
              <Reveal
                key={v.title}
                delay={i * 0.1}
                data-testid={`about-value-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div
                  data-testid={`about-value-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group h-full rounded-[24px] border border-white/10 bg-white/5 hover:bg-white/10 backdrop-blur-sm p-8 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-editorial italic text-brand-accent text-5xl leading-none tabular-nums">
                      {v.n}
                    </span>
                    <v.icon className="h-6 w-6 text-brand-sage" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-8 font-display text-2xl font-medium leading-snug">{v.title}</h3>
                  <p className="mt-4 text-brand-bg/80 leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION QUOTE */}
      <section className="py-28 md:py-36">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow className="text-brand-sage">05, the mission</Eyebrow>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 font-editorial italic text-brand-ink text-3xl sm:text-4xl md:text-5xl leading-[1.15]">
              &ldquo;Helping parents create systems that reduce overwhelm and make school life easier.&rdquo;
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12">
              <MagneticButton strength={14}>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-brand-ink hover:text-brand-sageDark transition-colors font-medium underline underline-offset-8 decoration-brand-sage/50 hover:decoration-brand-sageDark decoration-2"
                >
                  Get in touch
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SOFT EMAIL OPT-IN */}
      <section className="px-4 sm:px-6 lg:px-8 pb-28">
        <Reveal>
          <div
            data-testid="about-optin"
            className="relative max-w-4xl mx-auto rounded-[32px] overflow-hidden bg-gradient-to-br from-brand-sage to-brand-sageDark text-white"
          >
            <div className="absolute inset-0 grain opacity-25 pointer-events-none" />
            <div className="relative p-10 md:p-16 text-center">
              <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-white/85">
                Free download
              </p>
              <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-4xl sm:text-5xl leading-[1.02]">
                Want the free <span className="font-editorial italic">weekly reset?</span>
              </h2>
              <p className="mt-4 text-white/85 max-w-xl mx-auto">
                Every Sunday, know exactly what&apos;s coming at school this week.
              </p>

              {done ? (
                <div
                  data-testid="about-optin-success"
                  className="mt-10 inline-flex items-center gap-2 font-medium bg-white/15 border border-white/25 rounded-full px-6 py-3"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  You&apos;re in. Check your inbox in the next minute.
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="about-optin-form"
                  className="mt-10 grid grid-cols-1 sm:grid-cols-5 gap-3 max-w-xl mx-auto"
                >
                  <input
                    data-testid="about-optin-name"
                    required
                    type="text"
                    placeholder="First name"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="sm:col-span-2 bg-white/95 text-brand-ink placeholder:text-brand-inkMuted rounded-full px-5 py-3.5 outline-none focus:ring-2 ring-white"
                  />
                  <input
                    data-testid="about-optin-email"
                    required
                    type="email"
                    placeholder="Email address"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="sm:col-span-2 bg-white/95 text-brand-ink placeholder:text-brand-inkMuted rounded-full px-5 py-3.5 outline-none focus:ring-2 ring-white"
                  />
                  <button
                    type="submit"
                    data-testid="about-optin-submit"
                    disabled={loading}
                    className="sm:col-span-1 bg-white text-brand-sageDark hover:bg-brand-bg transition-colors px-5 py-3.5 rounded-full font-semibold disabled:opacity-70"
                  >
                    {loading ? "..." : "Send it"}
                  </button>
                </form>
              )}
              <p className="mt-6 text-xs text-white/70">No spam. Unsubscribe anytime.</p>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
